import type { CalendarEvent } from "../types";

export function toISODate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

type RecurrableEvent = Pick<CalendarEvent, "startDate" | "endDate" | "recurrence" | "recurrenceCount">;

// Whether an event (single-day, multi-day, or recurring) touches a given
// calendar day. Recurring events are always single-day (endDate === startDate
// is enforced when they're created), so a recurring event only "occurs" on
// days matching its weekday/day-of-month pattern, never as a spanning range.
export function occursOnDate(ev: RecurrableEvent, dateISO: string): boolean {
  if (ev.recurrence === "none" || !ev.recurrence) {
    return dateISO >= ev.startDate && dateISO <= ev.endDate;
  }
  if (dateISO < ev.startDate) return false;
  const start = new Date(ev.startDate + "T00:00");
  const target = new Date(dateISO + "T00:00");

  // `index` is 0 for the anchor occurrence (startDate) itself, 1 for the
  // next, etc. — so "ends after N occurrences" means index must be < N.
  let matches: boolean;
  let index: number;
  if (ev.recurrence === "weekly") {
    const diffDays = Math.round((target.getTime() - start.getTime()) / 86400000);
    matches = diffDays % 7 === 0;
    index = diffDays / 7;
  } else if (ev.recurrence === "monthly") {
    matches = target.getDate() === start.getDate();
    index = (target.getFullYear() - start.getFullYear()) * 12 + (target.getMonth() - start.getMonth());
  } else if (ev.recurrence === "yearly") {
    matches = target.getDate() === start.getDate() && target.getMonth() === start.getMonth();
    index = target.getFullYear() - start.getFullYear();
  } else {
    return false;
  }
  if (!matches) return false;
  if (ev.recurrenceCount != null && index >= ev.recurrenceCount) return false;
  return true;
}

// First date on/after `fromISO` that the event occurs on, or null if it
// never occurs on/after that date (a past one-off event). Bounded to
// scanning ~a year forward so it can't loop indefinitely.
export function nextOccurrenceOnOrAfter(ev: RecurrableEvent, fromISO: string): string | null {
  if (ev.recurrence === "none" || !ev.recurrence) {
    return ev.endDate >= fromISO ? ev.startDate : null;
  }
  const startISO = ev.startDate > fromISO ? ev.startDate : fromISO;
  const cursor = new Date(startISO + "T00:00");
  for (let i = 0; i < 370; i++) {
    const iso = toISODate(cursor);
    if (occursOnDate(ev, iso)) return iso;
    cursor.setDate(cursor.getDate() + 1);
  }
  return null;
}
